import { collection, addDoc, serverTimestamp, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../firebase';

/**
 * Create a new notification in Firestore
 * @param {Object} notificationData - Notification data
 * @param {string} notificationData.title - Notification title
 * @param {string} notificationData.message - Notification message/body
 * @param {string} notificationData.type - Notification type (success, warning, info, reminder, document, loan)
 * @param {string} notificationData.link - Link to navigate when clicked
 * @returns {Promise<string>} - Document ID of created notification
 */
export const createNotification = async (notificationData) => {
  try {
    const { title, message, type = 'info', link = null } = notificationData;

    if (!title || !message) {
      throw new Error('Title and message are required');
    }

    const notificationRef = collection(db, 'notifications');
    const docRef = await addDoc(notificationRef, {
      title,
      message,
      body: message, // Alternative field name for compatibility
      type,
      link,
      url: link, // Alternative field name for compatibility
      createdAt: serverTimestamp(),
      readBy: [], // Empty array - no one has read it yet
    });

    return docRef.id;
  } catch (error) {
    // Silent fail
    throw error;
  }
};

/**
 * Create a test notification for testing the notification system
 * @returns {Promise<string>} - Document ID of created test notification
 */
export const createTestNotification = async () => {
  const testNotifications = [
    {
      title: '🎉 New Loan Application',
      message: 'John Doe applied for a personal loan of ₹50,000. Review the application now.',
      type: 'loan',
      link: '/personal-loans',
    },
    {
      title: '✅ Loan Approved',
      message: 'Personal Loan #12345 has been approved successfully.',
      type: 'success',
      link: '/personal-loans',
    },
    {
      title: '⏰ Pending Review',
      message: 'You have 5 loan applications pending review.',
      type: 'warning',
      link: '/dashboard',
    },
    {
      title: '📄 Documents Updated',
      message: 'New documents uploaded for Business Loan #67890.',
      type: 'document',
      link: '/business-loans',
    },
    {
      title: '🔔 Reminder',
      message: 'Don\'t forget to review today\'s loan applications.',
      type: 'reminder',
      link: '/dashboard',
    },
  ];

  // Pick a random test notification
  const randomNotification = testNotifications[Math.floor(Math.random() * testNotifications.length)];

  const docId = await createNotification(randomNotification);
  
  return docId;
};

/**
 * Get all notifications (for debugging)
 * @param {number} limitCount - Number of notifications to fetch
 * @returns {Promise<Array>} - Array of notification objects
 */
export const getAllNotifications = async (limitCount = 25) => {
  try {
    const notificationsRef = collection(db, 'notifications');
    const notificationsQuery = query(
      notificationsRef,
      orderBy('createdAt', 'desc'),
      limit(limitCount)
    );

    const snapshot = await getDocs(notificationsQuery);
    const notifications = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return notifications;
  } catch (error) {
    // Silent fail
    throw error;
  }
};

/**
 * Test the entire notification system
 * Creates a test notification and fetches all notifications
 */
export const testNotificationSystem = async () => {
  try {
    // Step 1: Create test notification
    const docId = await createTestNotification();

    // Step 2: Wait a moment for Firestore to process
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Step 3: Fetch all notifications
    const allNotifications = await getAllNotifications(10);

    return { success: true, notificationId: docId, totalNotifications: allNotifications.length };
  } catch (error) {
    // Silent fail
    throw error;
  }
};
